import API from 'app/services/user/userApi'
import { mapUserFields } from 'app/actions/user'
import { AggregatedUserModel, UserModel } from 'app/models/userModel'

/**
 * Zapytania użytkowników, wyjęte ze slice'a `users`.
 *
 * Slice trzymał w jednym worku cztery niezależne rzeczy: pełną listę (`users`),
 * listę firmy (`companyUsers`), dane zalogowanego (`userInfo`) i licznik. Karmiły je
 * osobne akcje, więc rozdzielamy je na osobne klucze — inaczej stronicowana lista
 * z zakładki Użytkownicy nadpisywała pełną listę czytaną przez Raporty i Firmy.
 *
 * `mapUserFields` jest importowane z `app/actions/user`, a nie skopiowane: to duże
 * mapowanie ról i grup, które musi zostać jedno.
 */
export const usersKey = (companyId?: string | null) =>
  ['admin', 'users', companyId ?? null] as const

export const companyUsersKey = (
  companyId?: string | null,
  tenantId?: string | null,
) => ['admin', 'companyUsers', companyId ?? null, tenantId ?? null] as const

export const fetchUsers = async (
  companyId?: string,
  tenantId?: string,
): Promise<UserModel[]> => {
  const response = await API.create().getUsers(companyId, tenantId)
  return (response.content.data as any[]).map((c) => mapUserFields(c))
}

/** Lista użytkowników firmy — ten sam endpoint, inny zakres i inny klucz. */
export const fetchCompanyUsers = async (
  companyId?: string,
): Promise<UserModel[]> => {
  const response = await API.create().getUsers(companyId)
  return (response.content.data as any[]).map((c) => mapUserFields(c))
}

/**
 * Stronicowana lista dla zakładki Użytkownicy. Bez klucza react-query: siedem
 * parametrów należy do komponentu (stronicowanie, sortowanie, szukanie), a ekran
 * trzyma wynik w swoim stanie.
 */
export const fetchPagedUsers = async (
  companyId?: string,
  tenantId?: string,
  take?: number,
  skip?: number,
  orderBy?: string,
  descending?: boolean,
  searchValue?: string,
  onlyBlocked?: boolean,
): Promise<AggregatedUserModel> => {
  const response = await API.create().getPagedUsers(
    companyId,
    tenantId,
    take,
    skip,
    orderBy,
    descending,
    searchValue,
    onlyBlocked,
  )
  const data = response.content.data
  return {
    users: (data.users as any[]).map((c) => mapUserFields(c)),
    usersCount: data.usersCount,
  }
}
